<script setup lang="ts">
import { ElMessage, ElMessageBox } from "element-plus";
import { computed, ref, watch } from "vue";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";

const dialogVisible = defineModel<boolean>({ required: true });

const props = defineProps<{
  existingSysOrgGuid?: string;
}>();

const emit = defineEmits<{
  (e: "import:complete", sysOrgGuid: string): void;
}>();

const jsonData = ref("");
const importMode = ref<"new" | "overwrite">(
  props.existingSysOrgGuid ? "overwrite" : "new",
);
const newSysOrgCode = ref("");
const newSysOrgName = ref("");
const importing = ref(false);

watch(dialogVisible, (visible) => {
  if (visible) {
    importMode.value = props.existingSysOrgGuid ? "overwrite" : "new";
    jsonData.value = "";
    newSysOrgCode.value = "";
    newSysOrgName.value = "";
  }
});

const canImport = computed(() => {
  if (!jsonData.value) return false;
  if (importMode.value === "new" && !newSysOrgCode.value) return false;
  return true;
});

const handleFileChange = (file: File) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    jsonData.value = e.target?.result as string;
  };
  reader.readAsText(file);
  return false;
};

const handleImport = async () => {
  if (!canImport.value) return;

  const confirmMsg =
    importMode.value === "overwrite"
      ? "This will DELETE all existing configuration for this organization and replace it with the imported data. This cannot be undone. Continue?"
      : "This will create a new organization from the JSON data. Continue?";

  try {
    await ElMessageBox.confirm(confirmMsg, "Confirm Import", {
      confirmButtonText: "Import",
      cancelButtonText: "Cancel",
      type: "warning",
    });
  } catch {
    return;
  }

  importing.value = true;
  try {
    const result = await apiClient.sysOrg.importSysOrgFromJson({
      jsonData: jsonData.value,
      mode: importMode.value,
      targetSysOrgGuid:
        importMode.value === "overwrite" ? props.existingSysOrgGuid : undefined,
      newSysOrgCode:
        importMode.value === "new" ? newSysOrgCode.value : undefined,
      newSysOrgName:
        importMode.value === "new"
          ? newSysOrgName.value || undefined
          : undefined,
    });
    ElMessage.success(result.message || "Import completed successfully!");
    emit("import:complete", result.sysOrgGuid ?? "");
    dialogVisible.value = false;
  } catch (error) {
    showError(error);
  } finally {
    importing.value = false;
  }
};
</script>

<template>
  <el-dialog
    v-model="dialogVisible"
    title="Import Organization from JSON"
    width="600px"
    :close-on-click-modal="false"
  >
    <el-form label-position="top">
      <el-form-item label="Import Mode">
        <el-radio-group v-model="importMode">
          <el-radio value="new">Create New Organization</el-radio>
          <el-radio v-if="existingSysOrgGuid" value="overwrite">
            Overwrite Existing Organization
          </el-radio>
        </el-radio-group>
      </el-form-item>

      <el-form-item v-if="importMode === 'new'" label="New Org Code" required>
        <el-input
          v-model="newSysOrgCode"
          placeholder="e.g. MYORG"
          maxlength="5"
          show-word-limit
        />
      </el-form-item>

      <el-form-item v-if="importMode === 'new'" label="New Org Name">
        <el-input
          v-model="newSysOrgName"
          placeholder="Leave blank to use name from export"
          maxlength="250"
        />
      </el-form-item>

      <el-form-item label="JSON Data">
        <el-upload
          :before-upload="handleFileChange"
          :show-file-list="false"
          accept=".json"
        >
          <el-button type="primary" plain>Choose JSON File</el-button>
        </el-upload>
        <el-input
          v-model="jsonData"
          type="textarea"
          :autosize="{ minRows: 6, maxRows: 15 }"
          placeholder="Or paste exported JSON here..."
          style="margin-top: 8px"
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="dialogVisible = false">Cancel</el-button>
      <el-button
        type="primary"
        :loading="importing"
        :disabled="!canImport"
        @click="handleImport"
      >
        {{ importing ? "Importing..." : "Import" }}
      </el-button>
    </template>
  </el-dialog>
</template>
